/**
 * BrandLogo — unified, auth-aware logo component.
 *
 * - Uses /logo.png (the real Stratakey logo image) — no placeholder icon
 * - Smart link: logged-in users → "/" (dashboard), guests → "/landing"
 * - Accepts size and text-colour props for flexible placement across the app
 */
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

export interface BrandLogoProps {
  /**
   * Controls the text colour class(es) for "Stratakey".
   * Defaults to dark text suitable for light backgrounds.
   * Pass e.g. "text-white" for dark/hero sections.
   */
  textClassName?: string;
  /** Logo image height — sm 24 px, md 32 px (default), lg 40 px */
  size?: "sm" | "md" | "lg";
  /** Extra className on the wrapping <a> element */
  className?: string;
  /** If true, clicking always navigates to this explicit destination */
  forceTo?: string;
}

const sizeMap: Record<NonNullable<BrandLogoProps["size"]>, { img: string; text: string }> = {
  sm: { img: "h-6",  text: "text-sm"  },
  md: { img: "h-8",  text: "text-lg"  },
  lg: { img: "h-10", text: "text-xl"  },
};

export function BrandLogo({
  textClassName,
  size = "md",
  className,
  forceTo,
}: BrandLogoProps) {
  const { user } = useAuth();
  const to = forceTo ?? (user ? "/" : "/landing");
  const s = sizeMap[size];

  return (
    <Link
      to={to}
      className={cn("flex items-center gap-2.5 group", className)}
      aria-label="Stratakey – go to home"
    >
      <img
        src="/logo.png"
        alt="Stratakey logo"
        className={cn(
          s.img,
          "w-auto object-contain rounded-xl drop-shadow-sm group-hover:scale-105 transition-transform"
        )}
      />
      <span
        className={cn(
          s.text,
          "font-bold tracking-tight",
          textClassName ?? "text-slate-900 dark:text-white"
        )}
      >
        Stratakey
      </span>
    </Link>
  );
}
